import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../../services/api';
import type { PendingApprovalListItem } from '../../../services/api';
import styles from './PendingApprovals.module.css';

// AB#2108 (Admin V1 P0-2) — reads the real backend list item (`submittedAt`,
// `documentCount`, `city`); the page previously read `submittedDate` /
// `documentsCount` / `ownerEmail`, which the API never sends ("Invalid Date").
function formatDate(value?: string | null): string {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
}

const PendingApprovalsPage: React.FC = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState<PendingApprovalListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const fetchApprovals = useCallback(async () => {
        try {
            setLoading(true);
            setError(false);
            const data = await apiService.getPendingApprovals();
            setItems(data);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchApprovals();
    }, [fetchApprovals]);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h1 className={styles.title}>Pending Approvals</h1>
                    <p className={styles.subtitle}>Review and approve new salon applications</p>
                </div>
            </div>

            {loading && (
                <div className={styles.loadingState}>
                    <div className="spinner" />
                    <span>Loading pending approvals...</span>
                </div>
            )}

            {error && !loading && (
                <div className={styles.emptyState}>
                    Failed to load approvals. Please refresh the page.
                </div>
            )}

            {!loading && !error && items.length === 0 && (
                <div className={styles.emptyState}>
                    No pending approvals at this time.
                </div>
            )}

            {!loading && !error && items.length > 0 && (
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Business Name</th>
                                <th>Type</th>
                                <th>Owner</th>
                                <th>City</th>
                                <th>Submitted</th>
                                <th>Documents</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item) => (
                                <tr key={item.businessId}>
                                    <td style={{ fontWeight: 600 }}>{item.businessName}</td>
                                    <td>{item.businessType || '-'}</td>
                                    <td>{item.ownerName}</td>
                                    <td>{item.city || '-'}</td>
                                    <td>{formatDate(item.submittedAt)}</td>
                                    <td>{item.documentCount}</td>
                                    <td><span className={styles.badge}>Pending</span></td>
                                    <td>
                                        <button
                                            className={styles.reviewBtn}
                                            onClick={() => navigate(`/pending-approvals/${item.businessId}`)}
                                        >
                                            Review
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default PendingApprovalsPage;
